import {
  GridColDef,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { GridInitialStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium';
import { PermissionScope } from 'types/apps';
import { PartSchemas } from 'types/partStatus';
import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { FieldValues } from 'react-hook-form';
import * as Yup from 'yup';
import { ReactElement, ReactNode } from 'react';
import { Icon as TablerIcon } from '@tabler/icons-react';
import { ColorOptions } from 'types/index';

export interface DatagridProps {
  [gridName: string]: GridInitialStatePremium | null;
}

export interface apiRefProps {
  current: GridApiPremium;
}

export enum DatagridNames {
  bomLinesLinesTableV2 = 'bomLinesLinesTableV2',
  bomsTable = 'bomsTable',
  bomsTableV2 = 'bomsTableV2',
  inventoryLocations = 'inventoryLocations',
  inventoryParts = 'inventoryParts',
  inventoryShipments = 'inventoryShipments',
  inventoryStockLots = 'inventoryStockLots',
  inventoryPartStockLotTab = 'inventoryPartStockLotTab',
  inventoryShipmentStockLotTab = 'inventoryShipmentStockLotTab',
  inventoryShipmentLines = 'inventoryShipmentLines',
  warehouseShipments = 'warehouseShipments',
  warehouseShipmentLines = 'warehouseShipmentLines',
  inventoryHandling = 'inventoryHandling',
  inventoryAllocations = 'inventoryAllocations',
  stockLotLotLedger = 'stockLotLotLedger',
  usersTable = 'usersTable',
  partBoms = 'partBoms',
  partSourcingAgent = 'partSourcingAgent',
  partsQuotes = 'partsQuotes',
  partsSupplierQuotes = 'partsSupplierQuotes',
  partsSuppliersListings = 'partsSuppliersListings',
  partsDocuments = 'partsDocuments',
  partsDocumentsDescribePart = 'partsDocumentsDescribePart',
  partsRfqs = 'partsRfqs',
  detailPartsQuotes = 'detailPartsQuotes',
  purchasingSourcingRequestsTable = 'purchasingSourcingRequestsTable',
  partLibraryTable = 'partLibraryTable',
  purchasingOrders = 'purchasingOrders',
  supplierOrders = 'supplierOrders',
  purchasingOrdersLines = 'purchasingOrdersLines',
  purchasingOrdersStatusLines = 'purchasingOrdersStatusLines',
  productionRuns = 'productionRuns',
  productionRunParts = 'productionRunParts',
  productionRunPartsDetailAlts = 'productionRunPartsDetailAlts',
  productionRunPartsDetailBomlines = 'productionRunPartsDetailBomlines',
  partOrderHistory = 'partOrderHistory',
  productionRunConsumedStock = 'productionRunConsumedStock',
  partAlternatives = 'partAlternatives',
  partAlternativesDescribeParts = 'partAlternativesDescribeParts',
  subOrgs = 'subOrgs',
  bomPurchasingSelects = 'bomPurchasingSelects',
  programPurchasingSelects = 'programPurchasingSelects',
  invoices = 'invoices',
  suppliers = 'suppliers',
  purchaseRules = 'purchaseRules',
  allocateStockLots = 'allocateStockLots',
  purchasingDialogQuantities = 'purchasingDialogQuantities',
  allocateToProductionRunSetRequests = 'allocateToProductionRunSetRequests',
  allocateToProductionRunPreviews = 'allocateToProductionRunPreviews',
  allocateToProductionRunPreviewFailures = 'allocateToProductionRunPreviewFailures',
  multiQuoteRequests = 'multiQuoteRequests',
  bomScrubbingDuplicateParts = 'bomScrubbingDuplicateParts',
  bomScrubbingNoMatchRows = 'bomScrubbingNoMatchRows',
  bomScrubbingOverageWarningParts = 'bomScrubbingOverageWarningParts',
  bomScrubbingNoCustomIdParts = 'bomScrubbingNoCustomIdParts',
  productionRunCreateConsumeStockEvents = 'productionRunCreateConsumeStockEvents',
  purchasingOrderSnapshots = 'purchasingOrderSnapshots',
  purchasingOrderSnapshotsLines = 'purchasingOrderSnapshotsLines',
  purchasingOrderSnapshotsKitting = 'purchasingOrderSnapshotsKitting',
  purchasingOrderSnapshotLinePurchaseLines = 'purchasingOrderSnapshotLinePurchaseLines',
  purchasingOrderSnapshotLineShipments = 'purchasingOrderSnapshotLineShipments',
  documentExtractedData = 'documentExtractedData',
  // snapshotApprovalGenerateReport is not a real datagrid, but was required for generateReport functionality
  snapshotApprovalGenerateReport = 'snapshotApprovalGenerateReport',
  purchaseRequests = 'purchaseRequests',
  purchaseOrders = 'purchaseOrders',
  purchaseOrderLines = 'purchaseOrderLines',
  purchaseOrderNoPartLines = 'purchaseOrderNoPartLines',
  purchaseOrderPartHistory = 'purchaseOrderPartHistory',
  purchaseOrderProgramHistory = 'purchaseOrderProgramHistory',
  purchaseRequestAlts = 'purchaseRequestAlts',
  supplierBills = 'supplierBills',
  supplierBillLines = 'supplierBillLines',
  supplierBillLinePurchaseLines = 'supplierBillLinePurchaseLines',
  queryResponse = 'queryResponse',
  teams = 'teams',
  customProperties = 'customProperties',
  documentExtractors = 'documentExtractors',
  kits = 'kits',
  kitRequestLines = 'kitRequestLines',
  kitRequestLineAlts = 'kitRequestLineAlts',
  kitRequestLineShipmentLines = 'kitRequestLineShipmentLines',
  addPartsToKitRequestAdjustQuantities = 'addPartsToKitRequestAdjustQuantities',
  tags = 'tags',
  rfqs = 'rfqs',
  rfqLines = 'rfqLines',
  quotes = 'quotes',
  quoteLines = 'quoteLines',
  parts2Org = 'parts2Org',
  parts2Public = 'parts2Public',
  parts2Both = 'parts2Both',
  partSpecifications = 'partSpecifications',
  altSearchDialog = 'altSearchDialog',
  supplierBillLinesForPart = 'supplierBillLinesForPart',
  allocatedParts = 'allocatedParts',
  allocatedPartsStockLots = 'allocatedPartsStockLots',
  v2OptimizeOffersSelectedLeadTimeTable = 'v2OptimizeOffersSelectedLeadTimeTable',
  cartBuyableParts = 'cartBuyableParts',
  cartNonBuyableParts = 'cartNonBuyableParts',
  cartOrderLines = 'cartOrderLines',
  rfqParts = 'rfqParts',
  simplifiedPurchasingRfqs = 'simplifiedPurchasingRfqs',
  simplifiedPurchasingCarts = 'simplifiedPurchasingCarts',
  relatedOrderedCarts = 'relatedOrderedCarts',
  relatedSupplierOrders = 'relatedSupplierOrders',
  relatedRfqs = 'relatedRfqs',
  bulkAddToCartQuantities = 'bulkAddToCartQuantities',
  cartOrderGenerateReport = 'cartOrderGenerateReport',
  customerApiKeys = 'customerApiKeys',
  productionRunPartsSimplified = 'productionRunPartsSimplified',
}

interface BaseExtendedGridColDef<T extends GridValidRowModel>
  extends GridColDef<T> {
  permissionScope?: PermissionScope;
  editPermissionScope?: PermissionScope;
  inlineEdit?: boolean;
  getMutationArgs?: (data: FieldValues, params: GridRenderEditCellParams) => {};
  mutationValidation?: Yup.SchemaOf<unknown, object>;
  mutationPreSubmit?: (
    data: FieldValues,
    params: GridRenderCellParams
  ) => FieldValues;
  mutationAsClientV2?: boolean;
  mutationInputElement?: ReactElement;
  getMutationDefaultValue?: (params: GridRenderEditCellParams) => unknown;
  additionalInputElementProps?: unknown;
  disableBulkInlineEdit?: boolean;
  allowEditWhileLocked?: boolean;
  mutationGetIsDisabled?: (params: GridRenderCellParams) => boolean;
  onEditSuccess?: (params: GridRenderCellParams) => void;
  showChart?: {
    alwaysVisible?: boolean;
    title?: string;
    description?: string;
    disabled?: boolean;
    accordionCardGroupTitle?: string;
    schemas?: PartSchemas[];
    valueOptions: {
      value: string;
      label: string;
      tooltip?: string;
      color?:
        | 'warning'
        | 'success'
        | 'error'
        | 'info'
        | 'primary'
        | 'secondary'
        | 'grey';
    }[];
  } | null;
  allowInBlended?: boolean;
  // This function is used to format the cell values for export.
  // Note: It currently only formats cells that are strings.
  exportFormatter?: (value: string) => string;
}

type InlineEditGridColDef<T extends GridValidRowModel> =
  BaseExtendedGridColDef<T> & {
    inlineEditRenderCell: true;
    useMutation: UseMutation<any>;
  };

type NonInlineEditGridColDef<T extends GridValidRowModel> =
  BaseExtendedGridColDef<T> & {
    inlineEditRenderCell?: false;
  };

export type ExtendedGridColDef<
  T extends GridValidRowModel = GridValidRowModel
> = InlineEditGridColDef<T> | NonInlineEditGridColDef<T>;

export enum DatagridFilterOperatorsValues {
  '=' = '=',
  '!=' = '!=',
  '>' = '>',
  '>=' = '>=',
  '<' = '<',
  '<=' = '<=',
  is = 'is',
  not = 'not',
  after = 'after',
  before = 'before',
  onOrAfter = 'onOrAfter',
  onOrBefore = 'onOrBefore',
  contains = 'contains',
  isEmpty = 'isEmpty',
  isNotEmpty = 'isNotEmpty',
  isAnyOf = 'isAnyOf',
  startsWith = 'startsWith',
  endsWith = 'endsWith',
  equals = 'equals',
}

export const DatagridNumberFilterOperatorsValues = {
  '=': DatagridFilterOperatorsValues['='],
  '!=': DatagridFilterOperatorsValues['!='],
  '>': DatagridFilterOperatorsValues['>'],
  '>=': DatagridFilterOperatorsValues['>='],
  '<': DatagridFilterOperatorsValues['<'],
  '<=': DatagridFilterOperatorsValues['<='],
  isEmpty: DatagridFilterOperatorsValues.isEmpty,
  isNotEmpty: DatagridFilterOperatorsValues.isNotEmpty,
  isAnyOf: DatagridFilterOperatorsValues.isAnyOf,
};

export type RowActionRequirement = {
  disabled: boolean;
  disabledReason?: string;
};

export interface RowActionBase<T> {
  getRowIcon?: (row?: T) => TablerIcon;
  getBatchIcon?: (rows?: T[]) => TablerIcon;
  getLabel: (row?: T) => string;
  getBatchLabel?: (rows?: T[]) => string;
  getRowTooltip?: (row?: T) => string;
  getBatchTooltip?: (selectedRowCount: number) => string;
  color?: ColorOptions;
  getColor?: (row?: T) => ColorOptions;
  onRowClick?: (row?: T) => void;
  onBatchClick?: (rows?: T[]) => void;
  permissionScope?: PermissionScope;
  disabled?: boolean;
  showInRowMenu?: boolean;
  showInBatchMenu?: boolean;
  isRowDisabled?: (row?: T) => boolean;
  isBatchDisabled?: (rows?: T[]) => boolean;
  hideRowAction?: (row?: T) => boolean;
  hideBatchAction?: (rows?: T[]) => boolean;
  trackingName?: string;
  getRowRequirements?: (row?: T) => RowActionRequirement[];
  getBatchRequirements?: (rows?: T[]) => RowActionRequirement[];
}

export type RowAction<T> = RowActionBase<T> &
  (
    | { Icon: TablerIcon; getRowIcon?: never; getBatchIcon?: never }
    | { Icon?: never; getRowIcon: (row: T) => TablerIcon }
  );

export interface VisibleColumnGroup {
  name: string;
  label: string;
  icon?: ReactNode;
  visibleColumns: string[];
  permissionScope?: PermissionScope;
}

export type DatagridRecordButtonProps = {
  label: string;
  disabled?: boolean;
  onClick?: () => void;
  permissionScope?: PermissionScope;
  trackingName?: string;
};
